import { localhost, prodIp, stageIp } from 'redux/axios.config'
import { theme } from './GlobalStyles'

export interface IStringIndexedObject<T> {
  [key: string]: T
}

export function imageLink(symbolName: string) {
  const IP = process.env.REACT_APP_ENV_MODE === 'prod' ? prodIp : process.env.REACT_APP_ENV_MODE === 'stage' ? stageIp : localhost
  try {
    if (symbolName.includes('-3') || symbolName.includes('-2') || symbolName.includes('-1')) {
      return `https://${IP}/static/symbols/ADABULL.png`
    } else {
      return `https://${IP}/static/symbols/${symbolName}.png`
    }
  } catch (err) {
    console.log(err)
    return `https://${IP}/static/symbols/ADABULL.png`
  }
}

export function imageLinkToEx(exchangeName: string) {
  const IP = process.env.REACT_APP_ENV_MODE === 'prod' ? prodIp : process.env.REACT_APP_ENV_MODE === 'stage' ? stageIp : localhost
  return `https://${IP}/static/exchanges/${exchangeName}.png`
}

export function makeEmptyBalance({ symbol, exchange }: IStringIndexedObject<string>) {
  return {
    _id: '0',
    exchange: exchange,
    symbol: symbol,
    available: 0,
    onOrder: 0,
    total: 0,
    date: Date.now(),
    reserve: 0,
    accountName: 'mainAccount'
  }
}

const dexTypes = ['JustSwap', 'TronTrade', 'JustSwapV2']

export function isDexType(exchange: string) {
  return !!dexTypes.find(T => T === exchange)
}

export function isDexTypeSwapName(exchange: string) {
  return isDexType(exchange) ? 'Dex' : exchange
}

export const getColorStatus = (status: string): string => {
  switch (status) {
    case 'new':
      return theme.yellow
    case 'done':
      return theme.green
    default:
      return theme.red
  }
}

export const numberToShort = (num: number): string | number => {
  const stringNum = '' + num
  if (stringNum.match('.')) {
    if (stringNum.length <= 6) {
      return num
    } else {
      const arr = stringNum.split('.')
      if (arr[0].length >= 5) {
        return arr[0]
      }
      return (
        arr[0] +
        '.' +
        arr[1]
          .split('')
          .filter((item, index) => index < 6 - (arr[0].length - 1))
          .join('')
      )
    }
  } else {
    return num
  }
}

export const numToShortText = (num: number): string | number => {
  if (num > 999_999_999_999) {
    return `${Math.round(num / 1_000_000_000_000)}T`
  }
  if (num > 999_999_999) {
    return `${Math.round(num / 1_000_000_000)}B`
  }
  if (num > 999_999) {
    return `${Math.round(num / 1_000_000)}M`
  }
  if (num > 999) {
    return `${Math.round(num / 1_000)}k`
  }
  // if (num < 1000) {
  //   return `${num.toFixed(6)}`
  // }
  return num
}

export const symbols: { name: string; precision: number }[] = [
  { name: 'XRP', precision: 1 },
  { name: 'BTC', precision: 6 },
  { name: 'ETH', precision: 4 },
  { name: 'EOS', precision: 2 },
  { name: 'XLM', precision: 1 },
  { name: 'LTC', precision: 5 },
  { name: 'ZEC', precision: 5 },
  { name: 'XEM', precision: 2 },
  // { name: 'BNB', precision: 3 },
  { name: 'WAVES', precision: 6 },
  { name: 'TRX', precision: 2 },
  { name: 'DASH', precision: 4 },
  { name: 'BCH', precision: 6 },
  { name: 'USDT', precision: 2 },
  { name: 'UAH', precision: 2 },
  { name: 'WBTC', precision: 5 },
  { name: 'BTT', precision: 1 },
  { name: 'WIN', precision: 1 },
  { name: 'SUN', precision: 1 },
  { name: 'LIVE', precision: 1 },
  { name: 'JST', precision: 1 },
  { name: 'WETH', precision: 5 },
  { name: 'DICE', precision: 1 },
  { name: 'USDT', precision: 2 },
  { name: 'DOGE', precision: 1 },
  { name: 'SHIB', precision: 0 },
  { name: 'NFT', precision: 6 },
  { name: 'STARLY', precision: 8 },
]

export const numByPrecision = (symbol: string, num: number): number => {
  const currentPrecision = symbols.find(sym => sym.name === symbol)?.precision
  if (currentPrecision) {
    return Math.ceil(num * Math.pow(10, currentPrecision)) / Math.pow(10, currentPrecision)
  } else {
    return num
  }
}

export const round = (num: number, symbol: string | number): number => {
  if (typeof symbol === 'string') {
    const countOfSymbols = Math.pow(10, symbols.find(sym => sym.name === symbol)?.precision || 6)

    return Math.trunc(num * countOfSymbols) / countOfSymbols
  } else {
    const countOfSymbols = Math.pow(10, symbol)

    return Math.trunc(num * countOfSymbols) / countOfSymbols
  }
}

export const cutNumByPrecision = (num: number, precision: number): number => {
  return Math.floor(num * 10 ** precision) / 10 ** precision
}

export const expNumToString = (num: string | number) => {
  if (typeof num === 'number') {
    if (String(num).indexOf('e') >= 0) {
      const afterDot = String(num).split(String(num).indexOf('-') ? '-' : '+')[1]
      if (String(num).split('e')[0].indexOf('.')) {
        return num.toFixed(+afterDot + (String(num).split('e')[0].split('.')[1] ? String(num).split('e')[0].split('.')[1].length : 0))
      } else {
        return num.toFixed(Number(afterDot))
      }
    } else {
      return String(num)
    }
  } else {
    return num
  }
}

export const getPercentage = (target: number, source: number) => {
  return Math.floor((target * 100) / source)
}

export const getHumanTime = (time: any) => {
  const seconds = ~~(time / 1000)
  const minutes = ~~(time / (1000 * 60))
  const hours = ~~(time / (1000 * 60 * 60))
  const days = ~~(time / (1000 * 60 * 60 * 24))
  if (time < 1000) {
    return `0s`
  }
  if (seconds < 60) {
    return `${seconds % 60}s`
  }
  if (minutes < 60) {
    return `${minutes}m ${seconds % 60}s`
  }

  if (hours < 24) {
    return `${hours}h ${minutes % 60}m`
  }

  if (days > 0) {
    return `${days}d`
  }
}

export function makeQueryParamString(paramObject: IStringIndexedObject<number | string | boolean | undefined | string[]>) {
  return Object.entries(paramObject)
    .map(([key, value]) =>
      key && typeof value !== 'undefined' ? (!Array.isArray(value) ? `${key}=${value}` : value.map(v => `${key}=${v}`).join('&')) : ''
    )
    .filter(Boolean)
    .join('&')
}

export const sleep = async (ms: number) => {
  return new Promise(resolve => {
    setTimeout(resolve, ms)
  })
}

export const makeHumanDate = (timestamp: number) => {
  const date = new Date(timestamp)
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const seconds = date.getSeconds().toString().padStart(2, '0')
  return `${hours}:${minutes}:${seconds}`
}

export const timeToHumanShort = (ms: number): string => {
  if (ms < 1000) {
    return `<1s`
  } else if (ms < 1000 * 60) {
    return `${Math.floor(ms / 1000)}s`
  } else if (ms < 1000 * 60 * 60) {
    return `${Math.floor(ms / (1000 * 60))}m`
  } else if (ms < 1000 * 60 * 60 * 24) {
    return `${Math.floor(ms / (1000 * 60 * 60))}h`
  } else {
    return `${Math.floor(ms / (1000 * 60 * 60 * 24))}d`
  }
}
export const timeToHumanShortWithMinutes = (ms: number): string => {
  if (ms < 1000) {
    return `<1s`
  } else if (ms < 1000 * 60) {
    return `${Math.floor(ms / 1000)}s`
  } else if (ms < 1000 * 60 * 60) {
    return `${Math.floor(ms / (1000 * 60))}m`
  } else if (ms < 1000 * 60 * 60 * 24) {
    const hours = Math.floor(ms / (1000 * 60 * 60))
    const minutesInt = ms - hours * 1000 * 60 * 60
    const minutes = Math.floor(minutesInt / (1000 * 60))
    return `${hours}h ${minutes}m`
  } else {
    return `${Math.floor(ms / (1000 * 60 * 60 * 24))} d`
  }
}
export const timeToHumanShorter = (ms: number): string => {
  if (ms < 1000) {
    return `<1s`
  } else if (ms < 1000 * 60) {
    return `${Math.floor(ms / 1000)}s`
  } else if (ms < 1000 * 60 * 60) {
    return `${Math.floor(ms / (1000 * 60))}m`
  } else if (ms < 1000 * 60 * 60 * 24) {
    const hours = Math.floor(ms / (1000 * 60 * 60))
    const minutesInt = ms - hours * 1000 * 60 * 60
    const minutes = Math.floor(minutesInt / (1000 * 60))
    return `${hours}:${minutes}h`
  } else {
    return `${Math.floor(ms / (1000 * 60 * 60 * 24))} d`
  }
}
export const getColorByTime = (ms: number): string => {
  if (ms <= 1000) {
    return `r`
  } else if (ms <= 1000 * 3) {
    return `r`
  } else {
    return `g`
  }
}
export const getColorByTimeLimit = (ms: number): string => {
  if (ms <= 3000) {
    return `r`
  } else if (ms <= 5000) {
    return `y`
  } else {
    return `g`
  }
}
export const getColorByIndexMarket = (i: number): string => {
  if (i < 0.15) {
    return `r`
  } else if (i < 0.50 ) {
    return `y`
  } else {
    return `g`
  }
}
export const getColorByIndexLimit = (i: number): string => {
  if (i < 0.1) {
    return `r`
  } else if (i < 0.25 ) {
    return `y`
  } else {
    return `g`
  }
}